import classNames from "classnames"

import { Analytics, SharedInteractionEvents } from "@bounce/analytics"
import { Paragraph1, Title2 } from "@bounce/design-system"
import { Button } from "@bounce/web-components"

import { StoreListCard } from "./StoreListCard/StoreListCard"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { StoreItemListStructuredData } from "@/components/Generic/StructuredData/StoreItemListStructuredData"
import { useStoreSuggestions } from "@/components/LuggageStorage/Store/hooks/useStoreSuggestions"
import { useSectionViewed } from "@/hooks/useSectionViewed"
import type { TopRatedPoiStore } from "@/libs/bounce/stores/types"
import { useUrl } from "@/libs/router"
import type { PageType } from "@/types"
import { useAppURL } from "@/utils/appURL"

import MapIcon from "@bounce/assets/icons/Map.svg"

type StoreListProps = {
  className?: string
  path: string
  cta?: string
  from: PageType
  searchQuery: string
  stores: TopRatedPoiStore[]
  subtitle?: React.ReactNode
  title: React.ReactNode
  renderStructuredData?: boolean
}

export const StoreList = ({
  className,
  cta,
  from,
  searchQuery,
  stores,
  path,
  title,
  subtitle,
  renderStructuredData = true,
}: StoreListProps) => {
  const { getUrl } = useUrl()
  const buildAppURL = useAppURL()
  const { sectionRef } = useSectionViewed({ name: "StoreList" })
  const { handleClickStore, handleGetStoreUrl } = useStoreSuggestions({ from, searchQuery })

  const searchHref = buildAppURL(getUrl({ path: "/s" }), { query: searchQuery })

  return (
    <section
      ref={sectionRef}
      className={classNames("mx-auto flex w-full max-w-screen-xl flex-col px-6 xl:px-0", className)}>
      <div className="max-w-screen-md">
        <Title2>{title}</Title2>
        {!!subtitle && <Paragraph1 className="mt-2 text-neutral-800">{subtitle}</Paragraph1>}
      </div>
      <ul className="mx-auto mt-6 grid grid-cols-1 gap-7 md:mx-0 md:grid-cols-2 md:items-stretch md:gap-8 lg:grid-cols-3">
        {stores.map(({ store, customContent }) => (
          <StoreListCard
            as="li"
            customContent={customContent}
            href={handleGetStoreUrl(store)}
            key={store.id}
            onClick={(ev, opts) => handleClickStore(ev, store, opts)}
            store={store}
          />
        ))}
      </ul>
      {!!cta && (
        <div className="mt-8 flex flex-wrap-reverse justify-between gap-8 md:gap-x-20 lg:mt-12">
          <Button
            as={SafeLink}
            className="max-w-full flex-1 md:flex-none"
            href={searchHref}
            iconClassName="w-5"
            LeftIcon={MapIcon}
            variant="secondary"
            onClick={() => Analytics.track(SharedInteractionEvents.CTAPressed, { cta: "StoreList", from })}>
            {cta}
          </Button>
        </div>
      )}
      {renderStructuredData && <StoreItemListStructuredData stores={stores.map(({ store }) => store)} path={path} />}
    </section>
  )
}
